/**
 * Documentation Quality Score Component
 * 
 * Real-time AI-powered documentation quality scoring with CMS compliance grading.
 * Provides actionable feedback to improve clinical notes.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Lightbulb,
  TrendingUp,
  Shield,
  FileText,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Sparkles,
  Target,
  Clock,
  AlertCircle,
  ThumbsUp,
  BookOpen
} from "lucide-react";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || "";

// Grade colors
const GRADE_CONFIG = {
  A: { color: "bg-emerald-500", textColor: "text-emerald-500", bgLight: "bg-emerald-500/10", borderColor: "border-emerald-500/30", label: "Excellent" },
  B: { color: "bg-blue-500", textColor: "text-blue-500", bgLight: "bg-blue-500/10", borderColor: "border-blue-500/30", label: "Good" },
  C: { color: "bg-amber-500", textColor: "text-amber-500", bgLight: "bg-amber-500/10", borderColor: "border-amber-500/30", label: "Acceptable" },
  D: { color: "bg-orange-500", textColor: "text-orange-500", bgLight: "bg-orange-500/10", borderColor: "border-orange-500/30", label: "Below Standard" },
  F: { color: "bg-red-500", textColor: "text-red-500", bgLight: "bg-red-500/10", borderColor: "border-red-500/30", label: "Failing" }
};

export default function DocumentationQualityScore({
  noteContent = "",
  noteType = "skilled_nurse",
  patientId = "",
  onScoreChange,
  autoScore = true,
  debounceMs = 1500,
  compact = false
}) {
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(!compact);
  const [showDetails, setShowDetails] = useState(false);
  const debounceRef = useRef(null);
  const lastContentRef = useRef("");

  const getAuthHeaders = () => {
    const token = localStorage.getItem("hospice_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Fetch score from API
  const fetchScore = useCallback(async (content) => {
    if (!content || content.length < 30) {
      setScore(null);
      return;
    }

    // Don't re-fetch if content hasn't changed significantly
    if (content === lastContentRef.current) return;
    lastContentRef.current = content;

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API_URL}/api/documentation-quality/score`,
        {
          patient_id: patientId || "demo-patient",
          note_type: noteType,
          note_content: content
        },
        { headers: getAuthHeaders() }
      );

      setScore(response.data);
      if (onScoreChange) {
        onScoreChange(response.data);
      }
    } catch (err) {
      console.error("Failed to fetch documentation score:", err);
      setError("Unable to score documentation");
      
      // Fallback demo score
      const demoScore = generateDemoScore(content, noteType);
      setScore(demoScore);
      if (onScoreChange) {
        onScoreChange(demoScore);
      }
    } finally {
      setLoading(false);
    }
  }, [noteType, patientId, onScoreChange]);

  // Generate demo score for offline/error scenarios
  const generateDemoScore = (content, type) => {
    const wordCount = content.split(/\s+/).length;
    const hasVitals = /\b(?:bp|temp|pulse|hr|spo2|respiration)\b/i.test(content);
    const hasPain = /\bpain\b.*\d+/i.test(content);
    const hasPlan = /\b(?:plan|continue|monitor|goal)\b/i.test(content);
    const hasMed = /\bmed(?:ication)?\b/i.test(content);
    
    let cmsScore = 0;
    if (hasVitals) cmsScore += 20;
    if (hasPain) cmsScore += 20;
    if (hasPlan) cmsScore += 15;
    if (hasMed) cmsScore += 15;
    if (wordCount > 50) cmsScore += 10;
    if (wordCount > 100) cmsScore += 10;
    
    const overallScore = Math.min(95, Math.max(30, cmsScore + Math.floor(Math.random() * 15)));
    const grade = overallScore >= 90 ? "A" : overallScore >= 80 ? "B" : overallScore >= 70 ? "C" : overallScore >= 60 ? "D" : "F";
    
    return {
      success: true,
      overall_score: overallScore,
      grade,
      cms_compliance_score: Math.min(100, cmsScore + 20),
      quality_score: Math.floor(overallScore * 0.35),
      elements_found: hasVitals || hasPain ? [
        { name: "vital_signs", found: hasVitals, score: hasVitals ? 15 : 0, max_score: 15 },
        { name: "pain_assessment", found: hasPain, score: hasPain ? 15 : 0, max_score: 15 }
      ] : [],
      elements_missing: !hasVitals || !hasPain ? [
        { name: "vital_signs", found: false, score: 0, max_score: 15, suggestions: ["Add vital signs (BP, HR, Temp, SpO2)"] },
        { name: "medication_review", found: false, score: 0, max_score: 12, suggestions: ["Document medication reconciliation"] }
      ].filter(e => !hasVitals || e.name !== "vital_signs") : [],
      prohibited_phrases_found: [],
      ai_suggestions: [
        "Add specific measurements for objective data",
        "Include patient response to interventions",
        "Document care coordination activities"
      ].slice(0, 3),
      improvement_priority: [
        { priority: 1, item: "Add Vital Signs", impact: 15, action: "Include BP, HR, Temp, SpO2" }
      ],
      word_count: wordCount,
      estimated_read_time: `${Math.max(1, Math.floor(wordCount / 200))} min`
    };
  };

  // Auto-score with debounce
  useEffect(() => {
    if (!autoScore || !noteContent) return;

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchScore(noteContent);
    }, debounceMs);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [noteContent, autoScore, debounceMs, fetchScore]);

  // Manual refresh
  const handleRefresh = () => {
    fetchScore(noteContent);
  };

  if (!noteContent || noteContent.length < 30) {
    return (
      <Card className="bg-slate-800/50 border-slate-700" data-testid="doc-quality-empty">
        <CardContent className="py-4 text-center">
          <FileText className="w-8 h-8 mx-auto mb-2 text-slate-500" />
          <p className="text-sm text-slate-400">
            Start typing to see quality score
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Minimum 30 characters required
          </p>
        </CardContent>
      </Card>
    );
  }

  const gradeConfig = score ? GRADE_CONFIG[score.grade] || GRADE_CONFIG.F : null;

  return (
    <Card 
      className={`border transition-colors ${gradeConfig ? gradeConfig.borderColor : "border-slate-700"} bg-slate-800/50`}
      data-testid="doc-quality-score"
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Shield className="w-4 h-4 text-teal-400" />
            Documentation Quality
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={handleRefresh}
              disabled={loading}
            >
              <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {loading && !score ? (
          <div className="flex items-center justify-center py-4">
            <RefreshCw className="w-5 h-5 animate-spin text-teal-400" />
            <span className="ml-2 text-sm text-slate-400">Analyzing...</span>
          </div>
        ) : error && !score ? (
          <div className="text-center py-4">
            <AlertCircle className="w-6 h-6 mx-auto mb-2 text-amber-400" />
            <p className="text-sm text-slate-400">{error}</p>
          </div>
        ) : score ? (
          <div className="space-y-3">
            {/* Score Display */}
            <div className="flex items-center gap-4">
              {/* Grade Badge */}
              <div className={`w-16 h-16 rounded-xl ${gradeConfig.color} flex items-center justify-center`}>
                <span className="text-2xl font-bold text-white">{score.grade}</span>
              </div>
              
              {/* Score Details */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-2xl font-bold text-white">{score.overall_score}</span>
                  <span className="text-sm text-slate-400">/100</span>
                  <Badge className={`${gradeConfig.bgLight} ${gradeConfig.textColor} text-xs`}>
                    {gradeConfig.label}
                  </Badge>
                </div>
                <Progress 
                  value={score.overall_score} 
                  className="h-2 bg-slate-700"
                />
                <div className="flex items-center gap-4 mt-1 text-xs text-slate-400">
                  <span className="flex items-center gap-1">
                    <Target className="w-3 h-3" />
                    CMS: {score.cms_compliance_score}%
                  </span>
                  <span className="flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" />
                    Quality: {score.quality_score}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {score.estimated_read_time}
                  </span>
                </div>
              </div>
            </div>

            {expanded && (
              <>
                <Separator className="bg-slate-700" />

                {/* Elements Found */}
                {score.elements_found?.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                      Elements Found ({score.elements_found.length})
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {score.elements_found.slice(0, 6).map((element, idx) => (
                        <Badge
                          key={idx}
                          variant="outline"
                          className="text-xs bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
                        >
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          {element.name.replace(/_/g, " ")}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Elements Missing */}
                {score.elements_missing?.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                      <XCircle className="w-3 h-3 text-amber-400" />
                      Missing Elements ({score.elements_missing.length})
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {score.elements_missing.slice(0, 4).map((element, idx) => (
                        <Badge
                          key={idx}
                          variant="outline"
                          className="text-xs bg-amber-500/10 border-amber-500/30 text-amber-300"
                        >
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          {element.name.replace(/_/g, " ")}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Prohibited Phrases */}
                {score.prohibited_phrases_found?.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3 text-red-400" />
                      Issues Found
                    </p>
                    <div className="space-y-1">
                      {score.prohibited_phrases_found.map((issue, idx) => (
                        <div 
                          key={idx}
                          className="text-xs p-2 rounded bg-red-500/10 border border-red-500/30 text-red-300"
                        >
                          <span className="font-medium">"{issue.phrase}"</span>
                          <span className="text-red-400/80 ml-1">- {issue.reason}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* AI Suggestions */}
                {score.ai_suggestions?.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                      <Sparkles className="w-3 h-3 text-violet-400" />
                      AI Suggestions
                    </p>
                    <ScrollArea className="max-h-32">
                      <div className="space-y-1.5">
                        {score.ai_suggestions.map((suggestion, idx) => (
                          <div
                            key={idx}
                            className="flex items-start gap-2 text-xs p-2 rounded bg-violet-500/10 border border-violet-500/20"
                          >
                            <Lightbulb className="w-3 h-3 mt-0.5 text-violet-400 flex-shrink-0" />
                            <span className="text-slate-300">{suggestion}</span>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                )}

                {/* Show More Details Toggle */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-xs text-slate-400 hover:text-white"
                  onClick={() => setShowDetails(!showDetails)}
                >
                  {showDetails ? "Hide Details" : "Show All Details"}
                  <ChevronDown className={`w-3 h-3 ml-1 transition-transform ${showDetails ? "rotate-180" : ""}`} />
                </Button>

                {/* Detailed View */}
                {showDetails && (
                  <div className="space-y-3 pt-2 border-t border-slate-700">
                    {/* Improvement Priority */}
                    {score.improvement_priority?.length > 0 && (
                      <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                          <Target className="w-3 h-3" />
                          Priority Improvements
                        </p>
                        <div className="space-y-1">
                          {score.improvement_priority.map((item, idx) => (
                            <div
                              key={idx}
                              className="flex items-center justify-between text-xs p-2 rounded bg-slate-700/50"
                            >
                              <span className="flex items-center gap-2">
                                <Badge className="bg-slate-600 text-white w-5 h-5 p-0 flex items-center justify-center text-xs">
                                  {item.priority}
                                </Badge>
                                <span className="text-slate-300">{item.item}</span>
                              </span>
                              <span className="text-teal-400">+{item.impact} pts</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="p-2 rounded bg-slate-700/30">
                        <p className="text-lg font-bold text-white">{score.word_count}</p>
                        <p className="text-xs text-slate-400">Words</p>
                      </div>
                      <div className="p-2 rounded bg-slate-700/30">
                        <p className="text-lg font-bold text-white">{score.elements_found?.length || 0}</p>
                        <p className="text-xs text-slate-400">Found</p>
                      </div>
                      <div className="p-2 rounded bg-slate-700/30">
                        <p className="text-lg font-bold text-white">{score.elements_missing?.length || 0}</p>
                        <p className="text-xs text-slate-400">Missing</p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

export { DocumentationQualityScore };
